/**
 * Session lifecycle management for the Fyncall Widget
 * Handles session creation, resumption, and validation
 */

import { getConfig, getConfigValue } from './config.js';
import {
    getState,
    setSession,
    setVisitorToken,
    setHasConversation,
    setState
} from './state.js';
import {
    getOrCreateVisitorId,
    saveSession as saveSessionToStorage,
    getSavedSession as getSavedSessionFromStorage,
    clearSavedSession as clearSavedSessionFromStorage,
    getVisitorInfo as getStoredVisitorInfo,
    cacheMessages,
    getCachedMessages,
    clearMessageCache,
    isSessionExpired,
    updateSessionActivity,
    updateLastSync,
    SESSION_TTL
} from '../utils/storage.js';
import {
    initMessageDB,
    getMessagesFromCache as getMessagesFromIndexedDB,
    saveMessagesToCache as saveMessagesToIndexedDB,
    clearSessionMessages as clearIndexedDBMessages,
    clearOldMessages
} from '../utils/messageCache.js';
import {
    createSession as apiCreateSession,
    getSessionMessages,
    validateSession as apiValidateSession
} from './api.js';
import {
    connectWebSocket,
    disconnectWebSocket,
    resetWebSocketSession
} from '../features/websocket.js';

/**
 * Session manager state
 */
let sessionCheckComplete = false;
let sessionInvalid = false;

/**
 * Get Shopify context for session (if available)
 * @param {Object} shopifyContext - Shopify context from state
 * @returns {Object|null} Shopify context for API
 */
function getShopifyContextForSession(shopifyContext) {
    if (!shopifyContext) return null;

    return {
        shop: shopifyContext.shop,
        customer: shopifyContext.customer ? {
            id: shopifyContext.customer.id,
            email: shopifyContext.customer.email,
            firstName: shopifyContext.customer.firstName,
            lastName: shopifyContext.customer.lastName,
            ordersCount: shopifyContext.customer.ordersCount,
            totalSpent: shopifyContext.customer.totalSpent
        } : null,
        cart: shopifyContext.cart ? {
            itemCount: shopifyContext.cart.item_count,
            totalPrice: shopifyContext.cart.total_price
        } : null
    };
}

/**
 * Build session data from current page
 * @param {Object} shopifyContext - Optional Shopify context
 * @returns {Object} Session data
 */
function buildSessionData(shopifyContext) {
    const sessionData = {
        page_url: window.location.href,
        page_title: document.title,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent
    };

    const shopifyData = getShopifyContextForSession(shopifyContext);
    if (shopifyData) {
        sessionData.shopify_context = shopifyData;
        console.log('[FyncallSession] Including Shopify context in session');
    }

    return sessionData;
}

/**
 * Create a new session
 * @param {Object} options - Session options
 * @param {Function} options.onStatusUpdate - Callback for status updates
 * @param {Function} options.onSessionCreated - Callback when session is created
 * @param {Function} options.onWelcomeMessage - Callback with welcome message
 * @param {Object} options.shopifyContext - Shopify context if available
 * @returns {Promise<Object|null>} Session data or null on error
 */
export async function createSession({
    onStatusUpdate,
    onSessionCreated,
    onWelcomeMessage,
    shopifyContext
} = {}) {
    if (onStatusUpdate) onStatusUpdate('Connecting...', 'info');

    // Initialize IndexedDB for message caching
    try {
        await initMessageDB();
        // Clean up old messages (older than 24 hours)
        await clearOldMessages(SESSION_TTL);
    } catch (error) {
        console.warn('[FyncallSession] IndexedDB init failed, will use localStorage fallback:', error);
    }

    // Check for existing session first
    const savedSession = getSavedSessionFromStorage();
    if (savedSession && savedSession.sessionId && savedSession.visitorToken) {
        // Check if session has expired (24h inactivity)
        if (isSessionExpired()) {
            console.log('[FyncallSession] Session expired after 24h inactivity, creating new session');
            clearSession();
        } else {
            console.log('[FyncallSession] Found valid saved session:', savedSession.sessionId);
            const resumed = await resumeSession({
                savedSession,
                onStatusUpdate,
                onSessionCreated,
                onWelcomeMessage
            });
            if (resumed) return resumed;
            // If resume failed, clear and continue to create new
            clearSession();
        }
    }

    try {
        const config = getConfig();
        const visitorId = getOrCreateVisitorId();
        const widgetConfigId = config.widgetConfigId || 1;
        const visitorInfo = getStoredVisitorInfo();
        const sessionData = buildSessionData(shopifyContext);

        const session = await apiCreateSession({
            visitorId,
            widgetConfigId,
            visitorInfo,
            sessionData
        });

        // Update state
        setSession(session.id);
        setVisitorToken(session.visitor_token);
        saveSessionToStorage(session.id, session.visitor_token);
        setHasConversation(false);

        console.log('[FyncallSession] Session created:', session.id);
        if (onStatusUpdate) onStatusUpdate('Connected!', 'success');
        if (onSessionCreated) onSessionCreated(session);

        // Connect WebSocket
        connectWebSocket({
            sessionId: session.id,
            visitorToken: session.visitor_token,
            onInvalidSession: handleInvalidSession
        });

        // Send welcome message
        const welcomeMessage = session.greeting_message ||
            config.welcomeMessage ||
            "Hi there! 👋 How can I help you today?";
        if (onWelcomeMessage) onWelcomeMessage(welcomeMessage);

        return session;
    } catch (error) {
        console.error('[FyncallSession] Session error:', error);
        if (onStatusUpdate) onStatusUpdate('Connection failed. Please refresh.', 'error');
        return null;
    }
}

/**
 * Resume an existing session
 * @param {Object} options - Resume options
 * @param {Object} options.savedSession - Saved session data
 * @param {Function} options.onStatusUpdate - Status callback
 * @param {Function} options.onSessionCreated - Session created callback
 * @param {Function} options.onMessagesLoaded - Messages loaded callback
 * @returns {Promise<Object|null>} Session data or null if resume failed
 */
export async function resumeSession({
    savedSession,
    onStatusUpdate,
    onSessionCreated,
    onMessagesLoaded
} = {}) {
    // STEP 1: Load cached messages immediately (instant display)
    // Try IndexedDB first (larger capacity), fall back to localStorage
    let cachedMessages = null;
    let loadedFromCache = false;

    try {
        cachedMessages = await getMessagesFromIndexedDB(savedSession.sessionId);
        if (cachedMessages && cachedMessages.length > 0) {
            console.log('[FyncallSession] Loaded', cachedMessages.length, 'messages from IndexedDB cache');
        }
    } catch (error) {
        console.warn('[FyncallSession] IndexedDB cache failed, trying localStorage:', error);
    }

    // Fall back to localStorage if IndexedDB didn't have messages
    if (!cachedMessages || cachedMessages.length === 0) {
        cachedMessages = getCachedMessages(savedSession.sessionId);
        if (cachedMessages && cachedMessages.length > 0) {
            console.log('[FyncallSession] Loaded', cachedMessages.length, 'messages from localStorage cache');
        }
    }

    if (cachedMessages && cachedMessages.length > 0 && onMessagesLoaded) {
        onMessagesLoaded(cachedMessages, { fromCache: true });
        loadedFromCache = true;
    }

    try {
        // STEP 2: Fetch from server to get any new messages
        const serverMessages = await getSessionMessages(
            savedSession.sessionId,
            savedSession.visitorToken
        );

        // STEP 3: Update state
        setSession(savedSession.sessionId);
        setVisitorToken(savedSession.visitorToken);
        updateSessionActivity(); // Update activity timestamp

        const hasConversation = (cachedMessages?.length > 0) || (serverMessages.length > 0);
        setHasConversation(hasConversation);

        console.log('[FyncallSession] Session resumed:', savedSession.sessionId);
        if (onStatusUpdate) onStatusUpdate('Connected!', 'success');
        if (onSessionCreated) {
            onSessionCreated({
                id: savedSession.sessionId,
                visitor_token: savedSession.visitorToken
            });
        }

        // Connect WebSocket
        connectWebSocket({
            sessionId: savedSession.sessionId,
            visitorToken: savedSession.visitorToken,
            onInvalidSession: handleInvalidSession
        });

        // STEP 4: Merge and update cache
        if (serverMessages.length > 0) {
            // Update both caches with server messages (server is source of truth)
            cacheMessages(savedSession.sessionId, serverMessages);
            try {
                await saveMessagesToIndexedDB(savedSession.sessionId, serverMessages);
            } catch (error) {
                console.warn('[FyncallSession] Failed to save to IndexedDB:', error);
            }
            updateLastSync();

            // If we loaded from cache, only add NEW messages to UI
            // Compare by content + sender_type since cached messages may have temporary IDs
            if (loadedFromCache) {
                const cachedSignatures = new Set(cachedMessages.map(m =>
                    `${m.sender_type}:${m.content?.substring(0, 50)}`
                ));
                const newMessages = serverMessages.filter(m =>
                    !cachedSignatures.has(`${m.sender_type}:${m.content?.substring(0, 50)}`)
                );
                if (newMessages.length > 0 && onMessagesLoaded) {
                    console.log('[FyncallSession] Loading', newMessages.length, 'new messages from server');
                    onMessagesLoaded(newMessages, { fromCache: false, append: true });
                }
            } else if (onMessagesLoaded) {
                // No cache, load all server messages
                onMessagesLoaded(serverMessages, { fromCache: false });
            }
        }

        return { id: savedSession.sessionId, visitor_token: savedSession.visitorToken };
    } catch (error) {
        console.log('[FyncallSession] Session resume failed:', error.message);

        // Even if server fails, we can still show cached messages
        if (loadedFromCache) {
            console.log('[FyncallSession] Using cached messages despite server error');
            setSession(savedSession.sessionId);
            setVisitorToken(savedSession.visitorToken);
            setHasConversation(true);
            updateSessionActivity(); // Still update activity
            return { id: savedSession.sessionId, visitor_token: savedSession.visitorToken };
        }

        clearSession();
        return null;
    }
}

/**
 * Check for existing session on widget mount
 * @returns {Promise<boolean>} Whether there's an existing conversation
 */
export async function checkExistingSession() {
    const savedSession = getSavedSessionFromStorage();
    if (!savedSession || !savedSession.sessionId || !savedSession.visitorToken) {
        sessionCheckComplete = true;
        setHasConversation(false);
        return false;
    }

    // Check if session has expired (24h inactivity)
    if (isSessionExpired()) {
        console.log('[FyncallSession] Pre-check - Session expired, clearing');
        clearSession();
        sessionCheckComplete = true;
        setHasConversation(false);
        return false;
    }

    try {
        const messages = await getSessionMessages(
            savedSession.sessionId,
            savedSession.visitorToken
        );

        const hasConversation = messages.length > 0;
        setHasConversation(hasConversation);
        sessionCheckComplete = true;

        console.log('[FyncallSession] Pre-check - Has conversation:', hasConversation, 'Messages:', messages.length);
        return hasConversation;
    } catch (error) {
        console.error('[FyncallSession] Pre-check failed:', error);
        sessionCheckComplete = true;
        setHasConversation(false);
        return false;
    }
}

/**
 * Validate current session with server
 * @returns {Promise<boolean>} Whether session is valid
 */
export async function validateSession() {
    const state = getState();
    if (!state.session || !state.visitorToken) {
        handleInvalidSession();
        return false;
    }

    const isValid = await apiValidateSession(state.session, state.visitorToken);
    if (!isValid) {
        handleInvalidSession();
    }
    return isValid;
}

/**
 * Handle invalid/stale session
 * @param {Function} onNewSession - Callback when new session is created
 */
export async function handleInvalidSession(onNewSession) {
    if (sessionInvalid) return;
    sessionInvalid = true;

    console.log('[FyncallSession] Handling invalid session, clearing stored data...');

    // Close WebSocket
    disconnectWebSocket(true);

    // Clear message cache for current session (both localStorage and IndexedDB)
    const state = getState();
    if (state.session) {
        clearMessageCache(state.session);
        try {
            await clearIndexedDBMessages(state.session);
        } catch (error) {
            console.warn('[FyncallSession] Failed to clear IndexedDB messages:', error);
        }
    }

    // Clear storage
    clearSavedSessionFromStorage();

    // Reset state
    setSession(null);
    setVisitorToken(null);
    setHasConversation(false);
    resetWebSocketSession();

    // Allow new session creation
    setTimeout(() => {
        sessionInvalid = false;
        if (onNewSession) {
            onNewSession();
        }
    }, 1000);
}

/**
 * Clear current session
 */
export async function clearSession() {
    // Clear message cache for current session (both localStorage and IndexedDB)
    const state = getState();
    if (state.session) {
        clearMessageCache(state.session);
        try {
            await clearIndexedDBMessages(state.session);
        } catch (error) {
            console.warn('[FyncallSession] Failed to clear IndexedDB messages:', error);
        }
    }

    clearSavedSessionFromStorage();
    setSession(null);
    setVisitorToken(null);
    setHasConversation(false);
}

/**
 * Check if session check is complete
 * @returns {boolean} Whether check is complete
 */
export function isSessionCheckComplete() {
    return sessionCheckComplete;
}

/**
 * Get current session ID
 * @returns {string|number|null} Session ID
 */
export function getCurrentSession() {
    return getState().session;
}

/**
 * Get current visitor token
 * @returns {string|null} Visitor token
 */
export function getCurrentVisitorToken() {
    return getState().visitorToken;
}

/**
 * Check if there's an active conversation
 * @returns {boolean} Has conversation
 */
export function hasConversation() {
    return getState().hasConversation;
}
